import { useEffect, useState } from "react";
import { Camera, RefreshCcw, AlertTriangle, PlusCircle, ArrowLeftRight } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { fmtMoney, fmtDate, statusLabel } from "../lib/format";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";

const totalsStr = (t) => Object.entries(t || {}).map(([c, v]) => fmtMoney(v, c)).join(" + ") || "—";

const SummaryChip = ({ icon: Icon, label, value, warn, testId }) => (
  <div className={`flex items-center gap-2 border px-3 py-2 ${warn && value > 0 ? "border-amber-400 bg-amber-50" : "border-stone-200 bg-white"}`} data-testid={testId}>
    <Icon size={15} className={warn && value > 0 ? "text-amber-600" : "text-[#1C4E35]"} strokeWidth={1.8} />
    <span className="text-xs text-stone-600">{label}</span>
    <span className="font-heading text-sm font-black text-[#0A0D0B]">{value}</span>
  </div>
);

export default function ReconciliationPage() {
  const { user } = useAuth();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const isAdmin = user?.role === "admin";

  const load = () => {
    setLoading(true);
    api.get("/reconciliation/report")
      .then((r) => setReport(r.data))
      .catch(() => toast.error("Uyum raporu yüklenemedi"))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
  }, []);

  const takeSnapshot = async () => {
    setBusy(true);
    try {
      const r = await api.post("/reconciliation/snapshot");
      toast.success(`Snapshot alındı (${r.data.orgCount} müşteri)`);
      load();
    } catch {
      toast.error("Snapshot alınamadı");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-40 items-center justify-center" data-testid="recon-loading">
        <div className="h-5 w-5 animate-spin rounded-full border-2 border-[#1C4E35] border-t-transparent" />
      </div>
    );
  }
  if (!report) return null;

  const { summary, rows, snapshotAt } = report;

  return (
    <div className="space-y-4" data-testid="reconciliation-page">
      <div className="flex flex-wrap items-center gap-3 border border-stone-200 bg-white p-4">
        <SummaryChip icon={PlusCircle} label="Yeni Sipariş" value={summary.newOrders} testId="recon-summary-new" />
        <SummaryChip icon={ArrowLeftRight} label="Durum Değişikliği" value={summary.statusChanges} testId="recon-summary-status" />
        <SummaryChip icon={AlertTriangle} label="Eksik Veri (Stant/Hol)" value={summary.missingDataOrgs} warn testId="recon-summary-missing" />
        <p className="text-xs text-stone-500" data-testid="recon-snapshot-date">
          {snapshotAt ? `Son snapshot: ${fmtDate(snapshotAt)}` : "Henüz snapshot alınmadı — karşılaştırma için snapshot alın"}
        </p>
        <div className="ml-auto flex gap-2">
          <Button variant="outline" size="sm" onClick={load} data-testid="recon-refresh-button" className="h-8 rounded-sm border-stone-300 text-xs hover:bg-stone-100 active:scale-95">
            <RefreshCcw size={13} className="mr-1.5" /> Yenile
          </Button>
          {isAdmin && (
            <Button size="sm" onClick={takeSnapshot} disabled={busy} data-testid="recon-snapshot-button" className="h-8 rounded-sm bg-[#1C4E35] text-xs text-white hover:bg-[#163D2A] active:scale-95">
              <Camera size={13} className="mr-1.5" /> {busy ? "Alınıyor..." : "Snapshot Al"}
            </Button>
          )}
        </div>
      </div>

      <div className="border border-stone-200 bg-white">
        <Table data-testid="recon-table">
          <TableHeader>
            <TableRow className="bg-stone-50">
              <TableHead className="text-xs">Müşteri</TableHead>
              <TableHead className="text-right text-xs">Portal (Canlı)</TableHead>
              <TableHead className="text-right text-xs">Snapshot</TableHead>
              <TableHead className="text-right text-xs">Yeni Sipariş</TableHead>
              <TableHead className="text-xs">Durum Değişimi</TableHead>
              <TableHead className="text-xs">Eksik Veri</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => {
              const hasChange = r.delta.newRefs.length > 0 || r.delta.statusChanges.length > 0;
              const missing = r.current.missingPlot + r.current.missingHall;
              return (
                <TableRow key={r.organisation} className={hasChange ? "bg-[#1C4E35]/5" : ""} data-testid={`recon-row-${r.organisation}`}>
                  <TableCell className="max-w-[280px] truncate text-sm font-medium" title={r.organisation}>{r.organisation}</TableCell>
                  <TableCell className="text-right text-sm">
                    {r.current.orders} sipariş
                    <span className="block text-[11px] text-stone-500">{totalsStr(r.current.totals)}</span>
                  </TableCell>
                  <TableCell className="text-right text-sm">
                    {r.snapshot ? (
                      <>
                        {r.snapshot.orders} sipariş
                        <span className="block text-[11px] text-stone-500">{totalsStr(r.snapshot.totals)}</span>
                      </>
                    ) : (
                      <span className="text-[11px] text-stone-400">Snapshot yok</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right text-sm">
                    {r.delta.newRefs.length > 0 ? (
                      <span className="border border-[#1C4E35]/40 bg-[#1C4E35]/10 px-2 py-0.5 text-[11px] font-semibold text-[#1C4E35]">+{r.delta.newRefs.length}</span>
                    ) : (
                      <span className="text-stone-400">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-xs">
                    {r.delta.statusChanges.length > 0 ? (
                      <div className="space-y-0.5">
                        {r.delta.statusChanges.slice(0, 3).map((c) => (
                          <p key={c.ref} className="text-[11px]" title={c.ref}>
                            <span className="text-stone-500">{c.ref.slice(-10)}:</span> {statusLabel(c.from)} → <strong>{statusLabel(c.to)}</strong>
                          </p>
                        ))}
                        {r.delta.statusChanges.length > 3 && <p className="text-[11px] text-stone-500">+{r.delta.statusChanges.length - 3} daha</p>}
                      </div>
                    ) : (
                      <span className="text-stone-400">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-xs">
                    {missing > 0 ? (
                      <span className="border border-amber-400 bg-amber-50 px-2 py-0.5 text-[11px] text-amber-800">
                        Stant: {r.current.missingPlot} · Hol: {r.current.missingHall}
                      </span>
                    ) : (
                      <span className="text-stone-400">—</span>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      <p className="text-[11px] text-stone-500">
        Not: Bu rapor, portal verisini (canlı COP31 API) son alınan snapshot ile karşılaştırır. Haftalık snapshot otomatik alınır ve yöneticilere e-posta gönderilir.
      </p>
    </div>
  );
}
